import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { formatEventTime, type EventSession } from './format-event-time';

/**
 * - `'stacked'` (default): renders title above details as two Text blocks.
 * - `'inline-title'`: renders title and details on a single line, joined by a
 *   middle dot (`"·"`). Useful in compact rows where vertical space is tight.
 */
export type EventTimeAppearance = 'stacked' | 'inline-title';

type EventTimeProps = {
  sessions: EventSession[];
  appearance?: EventTimeAppearance;
  /**
   * Optional render prop for layout control. Receives the formatted parts so
   * the caller can lay them out themselves (e.g. into a `<Text>` with a
   * different style or into a non-`<Text>` element).
   *
   * Put `suppressHydrationWarning` on the element that directly holds `title`
   * or `details`: both are the visitor's browser's `Intl` wording, which can
   * differ from the server's (see "Intl text and hydration" in CLAUDE.md).
   */
  children?: (parts: {
    title: string;
    details: string | null;
  }) => ReactNode;
};

/**
 * Shared renderer for an event's date/time row across the storefront. Encodes
 * the multi-session / multi-day rules defined in `.plans/home-page.md`
 * (Phase 1c).
 *
 * Pass the event's full `sessions` array — the component derives the right
 * presentation kind from `sessions.length` and each session's start/end days.
 */
export function EventTime({
  sessions,
  appearance = 'stacked',
  children,
}: EventTimeProps) {
  const intl = useIntl();
  const parts = formatEventTime(sessions, intl);

  if (children) return <>{children(parts)}</>;

  // Defensive bail: with no sessions we'd otherwise emit an empty `<Text>` or
  // a stray middle-dot. Callers that pass `children` opt into rendering their
  // own empty state and are handled above.
  if (parts.title === '') return null;

  if (appearance === 'inline-title') {
    return (
      <Text
        style="body-large-regular"
        className={firstLetterCapStyle}
        suppressHydrationWarning
      >
        {parts.title}
        {parts.details ? ` · ${parts.details}` : ''}
      </Text>
    );
  }

  return (
    <Stack gap="tiny" className={firstLetterCapStyle}>
      <Text as="p" style="body-large-regular" suppressHydrationWarning>
        {parts.title}
      </Text>
      {parts.details && (
        <Text
          as="p"
          style="body-large-regular"
          color="secondary"
          suppressHydrationWarning
        >
          {parts.details}
        </Text>
      )}
    </Stack>
  );
}

/**
 * Capitalises the rendered title's first letter — many locales (e.g. `da-DK`)
 * emit lowercase weekday names ("fre, 23 juni 2026"), but the design wants
 * leading caps. Targets both `:first-child::first-letter` (the title `<Text>`
 * in a stacked layout) and `::first-letter` (the inline `<Text>` itself), so
 * the same selector works regardless of how callers wrap the output.
 *
 * Exported so other call sites that compose `<EventTime>` via render-prop
 * (e.g. `<UpcomingDates>`) reuse the exact same rule rather than defining a
 * parallel copy that could drift.
 */
export const firstLetterCapStyle = css({
  '& :first-child::first-letter': { textTransform: 'capitalize' },
  '&::first-letter': { textTransform: 'capitalize' },
});
